import type { CSSProperties } from "react";

interface SpinnerProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  trackColor?: string;
  indicatorColor?: string;
  label?: string;
}

const SIZE_MAP: Record<NonNullable<SpinnerProps["size"]>, number> = {
  sm: 16,
  md: 24,
  lg: 32,
};

export function Spinner({
  className = "",
  size = "md",
  trackColor = "rgba(99, 102, 241, 0.18)",
  indicatorColor = "#6366F1",
  label,
}: SpinnerProps) {
  const dimension = SIZE_MAP[size];
  const strokeWidth = size === "sm" ? 2.5 : size === "md" ? 3 : 3.5;
  const radius = (dimension - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.65;

  const spinnerStyle: CSSProperties = {
    width: dimension,
    height: dimension,
  };

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      role="status"
      aria-live="polite"
    >
      <svg
        className="animate-spin"
        viewBox={`0 0 ${dimension} ${dimension}`}
        style={spinnerStyle}
        data-testid="spinner"
      >
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          fill="none"
          stroke={indicatorColor}
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
        />
      </svg>
      {label && (
        <span
          className="text-sm text-slate-500 dark:text-slate-300"
          data-testid="spinner-label"
        >
          {label}
        </span>
      )}
    </div>
  );
}
